import React from 'react';
import { useIntl } from 'react-intl';
import { Buildings, CheckCircle, MapPin } from '@phosphor-icons/react';
import { CompanyLogo } from '../../../../../components/CompanyLogo';
import { CompanyFollowButton } from '../../../../../components/company/CompanyFollowButton';
import { ApplyButton } from '../components';
import { Link } from '../../../../../components/Link';
import { fixMojibake } from '../../../../../utils/text/fix-mojibake';
import { resolveSearchHitHostedLogoUrl } from '../../../../../utils/search/resolve-search-hit-company-display';
import type { DisplayJob } from '../../../../../types';
import { Msg, useFormatMessage } from '../../../../../i18n-review/client/Msg';

export interface BrandRailProps {
  displayJob: DisplayJob;
  panelColor: string;
  panelOn: string;
  /** Brand hue darkened enough to read clearly as text on the white "Apply" pill — see adjustBrandColor. */
  accentOnWhite: string;
  applyUrl: string;
  isApplied: boolean;
  setAppliedJobState: (job: any, applied: boolean) => Promise<void>;
  onTailorCv?: () => void;
  /** Real, employer-quoted monthly-EUR pay (already formatted) — only set for `salaryListingProvenance === 'employer_quote'`, never for a modelled estimate. TEC-509: surfaced directly above Apply, not just in the collapsible salary-comparison section further down, per Duunitori's own market survey (28% of Finnish job seekers wouldn't apply without visible pay). */
  realSalaryLabel?: string | null;
  formattedPostedDate: string | null;
  postedDaysAgo: number | null;
  formattedLastSeenDate: string | null;
  formattedApplicationDate: string | null;
  formattedEstimatedExpiryDate: string | null;
  companyId?: string | null;
  companyName?: string;
  isTyomarkkinatori?: boolean;
  location?: string;
  /** Set only when the company profile is linkable (shouldLinkCompanyProfile) — see JobDetailPageV2.tsx. */
  companyProfileHref?: string;
  companyLinkTitle?: string;
  insightsCueLabel?: string;
  locale?: string;
}

/**
 * Left rail for the redesigned (V2) job detail page: logo, company identity (name, insights
 * link, location), a white "Apply now" pill, and posted/last-seen/expiry dates pinned to the
 * bottom. The full salary picture (estimates, comparisons, benefits) still lives in the
 * collapsible Salary Context section further down, where the real-vs-estimated distinction can
 * be shown properly — but TEC-509 surfaces the one number worth showing regardless, real
 * employer-quoted pay (never a modelled estimate), directly above Apply too, since that section
 * can be collapsed or scrolled past. See `realSalaryLabel` below.
 */
export function BrandRail({
  displayJob,
  panelColor,
  panelOn,
  accentOnWhite,
  applyUrl,
  isApplied,
  setAppliedJobState,
  onTailorCv,
  realSalaryLabel,
  formattedPostedDate,
  postedDaysAgo,
  formattedLastSeenDate,
  formattedApplicationDate,
  formattedEstimatedExpiryDate,
  companyId,
  companyName,
  isTyomarkkinatori = false,
  location,
  companyProfileHref,
  companyLinkTitle,
  insightsCueLabel,
  locale,
}: BrandRailProps): React.ReactElement {
  const intl = useIntl();
  const formatMessage = useFormatMessage();

  const applyBtnStyle: React.CSSProperties = {
    backgroundColor: '#FFFFFF',
    color: accentOnWhite,
  };

  return (
    <div
      className="relative flex w-full flex-none flex-col items-center px-4 py-4 lg:px-5 lg:py-6 box-border lg:w-[210px] rounded-t-[24px] lg:rounded-l-[24px] lg:rounded-tr-none"
      style={{ background: panelColor, color: panelOn }}
    >
      {/* Desktop only: the whole identity block (logo, name, insights link, location, follow
          button, apply button) stays together and stays visible while scrolling, instead of
          just scrolling away with the rest of the rail. The rail itself is stretched
          (items-stretch) to match the long description column's height, and this is bounded
          within that box so it can't stick past the bottom of the job card. Mobile gets its
          own crossfade mini-header instead (JobDetailPageV2.tsx), matching the native app's
          job screen, since the rail isn't stretched full-height in the stacked mobile layout. */}
      <div className="relative flex w-full flex-none flex-col items-center lg:sticky lg:top-6 lg:z-10">
        {companyId && !isTyomarkkinatori ? (
          // Desktop rail is a fixed 210px column with the logo centered in it, so a right-4
          // badge here would sit ~27px inside the logo's right edge — pushed further right
          // (into the rail's own outer padding, which has room to spare) so it clears the tile
          // instead of overlapping it, where its translucent onBrand fill goes invisible.
          <div className="absolute right-4 top-4 lg:-right-4 lg:top-3">
            <CompanyFollowButton compact compactSize={44} onBrand companyId={companyId} companyName={companyName} surface="job-page" />
          </div>
        ) : null}
        <div className="flex h-[72px] w-[72px] lg:h-[104px] lg:w-[104px] flex-none items-center justify-center rounded-[18px] lg:rounded-[22px] bg-white p-2.5 lg:p-3.5 box-border overflow-hidden">
          <CompanyLogo
            companyName={displayJob.companyId || displayJob.companyName || ''}
            initialsSourceName={displayJob.companyName ?? displayJob.companyId}
            registryCompanyFormDescription={displayJob.registryCompanyFormDescription}
            isTyomarkkinatori={displayJob.isTyomarkkinatori}
            hostedLogoUrl={resolveSearchHitHostedLogoUrl(displayJob as unknown as Record<string, unknown>)}
            variant="bare"
            size={104}
            loading="eager"
          />
        </div>

        <div className="mt-2.5 lg:mt-4 flex flex-col items-center gap-1 lg:gap-1.5 text-center w-full min-w-0">
          {companyName ? (
            <div className="[font-family:'Space_Grotesk',sans-serif] text-[15px] font-bold leading-tight break-words">
              {fixMojibake(companyName)}
            </div>
          ) : null}
          {companyProfileHref ? (
            <Link
              href={companyProfileHref}
              locale={locale}
              title={companyLinkTitle}
              className="inline-flex items-center gap-1 text-[11px] font-semibold opacity-90 hover:opacity-100 hover:underline underline-offset-2"
            >
              <Buildings weight="fill" className="h-3 w-3 shrink-0" aria-hidden />
              <span>{insightsCueLabel ?? formatMessage({ id: 'hit.companyInsights.cue', defaultMessage: 'Company insights', description: 'Short label under the employer name on a job card; the block links to the company profile' })}</span>
            </Link>
          ) : null}
          {location ? (
            <div className="mt-0.5 inline-flex items-center gap-1 text-[11px] font-medium opacity-90 break-words">
              <MapPin weight="fill" className="h-3 w-3 shrink-0" aria-hidden />
              <span>{fixMojibake(location)}</span>
            </div>
          ) : null}
        </div>

        {realSalaryLabel ? (
          <div
            className="mt-3 lg:mt-5 flex w-full items-center justify-center gap-1.5 rounded-xl bg-white/95 px-2.5 py-2 text-center"
            style={{ color: accentOnWhite }}
            aria-label={`${realSalaryLabel} ${formatMessage({
              id: 'jobDetailPageV2.realSalaryNote',
              defaultMessage: '— real pay, quoted by the employer',
              description: "Note next to the prominent real-salary figure shown above the collapsible salary comparison section, clarifying it's from the employer and not an estimate",
            })}`}
          >
            <CheckCircle weight="fill" className="h-4 w-4 shrink-0" aria-hidden />
            <span className="text-[12.5px] font-bold leading-tight [font-family:'Space_Grotesk',sans-serif]" aria-hidden>
              {realSalaryLabel}
            </span>
          </div>
        ) : null}

        <div className={realSalaryLabel ? 'mt-2 w-full' : 'mt-3 lg:mt-5 w-full'}>
          <ApplyButton
            applyUrl={applyUrl}
            isApplied={isApplied}
            displayJob={displayJob}
            applyBtnStyle={applyBtnStyle}
            applyBtnHoverStyle={{ filter: 'brightness(0.96)' }}
            effectiveBrandColour={panelColor}
            onSetApplied={setAppliedJobState}
            onTailorCv={onTailorCv}
            compact
          />
        </div>
      </div>

      {/* Mobile only: marks where the rail's own (non-sticky) identity block ends, so
          JobDetailPageV2's IntersectionObserver knows when to fade in the crossfade
          mini-header. Placed after the sticky wrapper (which mobile ignores, no lg:sticky
          effect below the lg breakpoint) so it tracks the true end of the rail's content. */}
      <div id="brand-rail-mobile-sentinel" className="lg:hidden" aria-hidden="true" />

      {(formattedPostedDate || formattedLastSeenDate || formattedApplicationDate || formattedEstimatedExpiryDate) ? (() => {
        const postedLine = formattedPostedDate
          ? formatMessage(
              { id: 'jobDetailPageV2.postedOn', defaultMessage: 'Posted {date}', description: 'Posted date shown in the redesigned job detail page brand rail' },
              { date: formattedPostedDate },
            ) +
            (postedDaysAgo != null
              ? ` · ${formatMessage({ id: 'jobDetailPageV2.daysAgo', defaultMessage: '{days, plural, one {# day ago} other {# days ago}}', description: 'Relative days-ago label' }, { days: postedDaysAgo })}`
              : '')
          : null;
        const lastSeenLine = formattedLastSeenDate
          ? formatMessage(
              { id: 'jobDetailPageV2.lastSeen', defaultMessage: 'Last seen {date}', description: 'Last-seen-in-crawl date shown in the redesigned job detail page brand rail' },
              { date: formattedLastSeenDate },
            )
          : null;
        const deadlineLine = formattedApplicationDate
          ? formatMessage(
              { id: 'jobDetailPageV2.deadline', defaultMessage: 'Deadline {date}', description: 'Application deadline shown in the redesigned job detail page brand rail' },
              { date: formattedApplicationDate },
            )
          : formattedEstimatedExpiryDate
            ? formatMessage(
                { id: 'jobDetailPageV2.estimatedExpiry', defaultMessage: 'Est. expiry {date}', description: 'Estimated listing expiry date shown in the redesigned job detail page brand rail' },
                { date: formattedEstimatedExpiryDate },
              )
            : null;
        const dateLines = [postedLine, lastSeenLine, deadlineLine].filter((line): line is string => Boolean(line));

        return (
          <>
            {/* Desktop: one line per date, stacked (room to spare in the rail). */}
            <div className="mt-auto hidden lg:flex flex-col items-center gap-1.5 pt-6 text-center text-[10.5px] font-semibold">
              {dateLines.map((line) => (
                <span key={line}>{line}</span>
              ))}
            </div>
            {/* Mobile: joined onto as few lines as possible — the rail is full-width above the
                content instead of a side column here, so stacking three separate lines cost real
                scroll distance before the job content even starts. */}
            <div className="mt-3 lg:hidden w-full text-center text-[10.5px] font-semibold leading-relaxed">
              {dateLines.join(' · ')}
            </div>
          </>
        );
      })() : null}
    </div>
  );
}
